import DOMPurify from 'dompurify'

import { last, wrap, isString } from 'lodash-es'
import { isText, toText, defaultTo } from './bits'

// toUrl & isUrl
// toSrc & isSrc
const fallbackToRelativeUrl = true
const fallbackToAbsoluteUrl = false

const noopStr = ''
const fallbackRelativeStr = document.location.pathname.replace(/\/[^/]*$/, '') + '/'
const fallbackProtocolStr = 'https://'

const dataRE = /^data:\w+\/[+\w]+;base64,[A-Za-z0-9+/]+={0,2}$/
const relativeRE = /^\/[^/]?/
const protocolRE = /^[a-z]+?:\/\//i
const javascriptRE = /^\s*j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:/i
const protocolLessRE = /^\/\//i
const protocolLikeRE = /^([a-z]+:\/\/|\/\/|\/)/i

const basicAuthRE = /^([a-z]+:\/\/|\/\/|\/)?[^/.]*:?[^/.]*@/i
const basicAuthReplacer = '$1'

const doubleSlashRE = /\/{2,}/g
const doubleSlashReplacer = '/'

const doubleQuoteRE = /"/g
const doubleQuoteReplacer = '%22'

const addProtocolRe = /^(\/\/)/i
const addProtocolReplacer = 'https://'

const dropProtocolRE = /^([a-z]+?:\/\/)/i
const dropProtocolReplacer = '//'

export function toUrl(v) {
  const raw = cleanUrl(v, fallbackToAbsoluteUrl)
  const url = addProtocol(raw)
  if (!isUrl(url)) {
    return null
  }

  return url
}

export function isUrl(v) {
  return isText(v) && (relativeRE.test(v) || protocolRE.test(v))
}

export function toSrc(v) {
  const raw = cleanUrl(v, fallbackToRelativeUrl)
  const src = dropProtocol(raw)
  if (!isSrc(src)) {
    return null
  }

  return src
}

export function isSrc(v) {
  return isText(v) && (dataRE.test(v) || relativeRE.test(v) || protocolLessRE.test(v))
}

function cleanUrl(v, fallbackToRelativeUrl) {
  const text = toText(v)
  if (!isText(text)) {
    return null
  }

  if (javascriptRE.test(text)) {
    return null
  }

  if (dataRE.test(text)) {
    return text
  }

  const prefixed = protocolLikeRE.test(text)
    ? text
    : fallbackToRelativeUrl
      ? fallbackRelativeStr + text
      : fallbackProtocolStr + text

  const unauthed = prefixed.replace(basicAuthRE, basicAuthReplacer)
  const unquoted = unauthed.replace(doubleQuoteRE, doubleQuoteReplacer)

  const protocol = defaultTo(last(protocolLikeRE.exec(unquoted)), noopStr)
  const mainPart = unquoted.slice(protocol.length)
  const unslashed = protocol + mainPart.replace(doubleSlashRE, doubleSlashReplacer)

  return unslashed
}

function addProtocol(v) {
  const text = toText(v)
  if (!isText(text)) {
    return null
  }

  return text.replace(addProtocolRe, addProtocolReplacer)
}

function dropProtocol(v) {
  const text = toText(v)
  if (!isText(text)) {
    return null
  }

  return text.replace(dropProtocolRE, dropProtocolReplacer)
}

// htmlToText
// htmlWithoutLinks
// htmlWithSafeLinks

export function htmlToText(rawOldHtml, count = 1, limit = 3) {
  if (!isString(rawOldHtml)) {
    return null
  }

  const elm = document.createElement('div')
  const oldHtml = toText(DOMPurify.sanitize(rawOldHtml))
  elm.innerHTML = oldHtml
  const newText = toText(elm.textContent)
  const completes = oldHtml === newText

  if (!completes && (count >= limit)) {
    return escapeHtml(newText)
  } else if (!completes) {
    return htmlToText(newText, count + 1, limit)
  } else {
    return newText
  }
}

export function htmlWithoutLinks(rawOldHtml, count = 1, limit = 3) {
  if (!isString(rawOldHtml)) {
    return null
  }

  const elm = document.createElement('div')
  const config = { USE_PROFILES: { html: true } }
  const oldHtml = toText(DOMPurify.sanitize(rawOldHtml, config))
  elm.innerHTML = oldHtml
  const newHtml = toText(replaceLinks(elm).innerHTML)
  const completes = isEqualElms(oldHtml, newHtml)

  if (!completes && (count >= limit)) {
    return htmlToText(newHtml)
  } else if (!completes) {
    return htmlWithoutLinks(newHtml, count + 1, limit)
  } else {
    return newHtml
  }
}

export function htmlWithSafeLinks(rawOldHtml, count = 1, limit = 3) {
  if (!isString(rawOldHtml)) {
    return null
  }

  const elm = document.createElement('div')
  const config = { USE_PROFILES: { html: true }, ADD_ATTR: ['target'] }
  const oldHtml = toText(DOMPurify.sanitize(rawOldHtml, config))
  elm.innerHTML = oldHtml
  const newHtml = toText(shieldLinks(elm).innerHTML)
  const completes = isEqualElms(oldHtml, newHtml)

  if (!completes && (count >= limit)) {
    return htmlToText(newHtml)
  } else if (!completes) {
    return htmlWithSafeLinks(newHtml, count + 1, limit)
  } else {
    return newHtml
  }
}

function replaceLinks(elm) {
  for (let index = elm.children.length - 1; index !== -1; index--) {
    const child = elm.children[index]
    if (child.tagName === 'A') {
      const text = toText(child.textContent)
      const replacer = document.createTextNode(text)
      elm.insertBefore(replacer, child)
      elm.removeChild(child)
    } else {
      replaceLinks(child)
    }
  }
  return elm
}

function shieldLinks(elm) {
  for (let index = elm.children.length - 1; index !== -1; index--) {
    const child = elm.children[index]
    if (child.tagName === 'A') {
      const url = toUrl(child.getAttribute('href'))
      if (isUrl(url)) {
        child.setAttribute('href', url)
        child.setAttribute('rel', 'noopener noreferrer')
        child.setAttribute('target', '_blank')
        child.setAttribute('tabindex', '-1')
      } else {
        const text = toText(child.textContent)
        const replacer = document.createTextNode(text)
        elm.insertBefore(replacer, child)
        elm.removeChild(child)
      }
    } else {
      shieldLinks(child)
    }
  }
  return elm
}

function isEqualElms(a, b) {
  const aElm = document.createElement('div')
  const bElm = document.createElement('div')
  aElm.innerHTML = a
  bElm.innerHTML = b
  return aElm.isEqualNode(bElm)
}

// unescapeHtml & escapeHtml
// escapeHtmlVerbose

const reUnescapedHtml = /[<>"']/g
const reHasUnescapedHtml = RegExp(reUnescapedHtml.source)
const escapeHtmlChar = strictPropertyOf({
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;'
})

const reEscapedHtml = /&lt;|&gt;|&quot;|&#39;/g
const reHasEscapedHtml = RegExp(reEscapedHtml.source)
const unescapeHtmlChar = strictPropertyOf({
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': '\''
})

export function unescapeHtml(rawText) {
  if (!isString(rawText)) {
    return null
  }

  const text = toText(rawText)
  if (!reHasEscapedHtml.test(text)) {
    return text
  }

  const html = text.replace(reEscapedHtml, unescapeHtmlChar)
  return html
}

export function escapeHtml(rawHtml) {
  if (!isString(rawHtml)) {
    return null
  }

  const html = toText(rawHtml)
  if (!reHasUnescapedHtml.test(html)) {
    return html
  }

  const text = html.replace(reUnescapedHtml, escapeHtmlChar)
  return text
}

export function escapeHtmlVerbose(rawHtml, changes) {
  if (!isString(rawHtml)) {
    return null
  }

  const html = toText(rawHtml)
  if (!reHasUnescapedHtml.test(html)) {
    return html
  }

  const text = html.replace(reUnescapedHtml, wrap(changes, verboseEscaper))
  return text
}

function verboseEscaper(changes, match, index) {
  const replacer = escapeHtmlChar(match)
  changes.push({
    index,
    match,
    replacer
  })

  return replacer
}

function strictPropertyOf(obj) {
  return function strictPropertyOf_wrapper(key) {
    return obj[key]
  }
}
